import React, { useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { isRealIngredient, scaleAmount } from "@workspace/recipe-utils";

const UNITS = [
  "", // blank
  "cup", "tbsp", "tsp",
  "oz", "lb",
  "can", "pkg", "jar", "bottle", "qt", "gallon",
  "piece", "clove", "strip", "dash", "pinch", "bunch", "sprig",
] as const;

export interface LocalIngredient {
  _key: string;
  id?: number;
  sortOrder: number;
  amount: string | null;
  unit: string | null;
  item: string;
  prepNotes: string | null;
  sectionHeader: string | null;
}

interface RowProps {
  ingredient: LocalIngredient;
  onChange: (updated: LocalIngredient) => void;
  onDelete: () => void;
  scaleFactor?: number;
}

function SortableRow({ ingredient, onChange, onDelete, scaleFactor = 1 }: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ingredient._key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const set = (field: keyof LocalIngredient, value: string) => {
    onChange({ ...ingredient, [field]: value || null });
  };

  const hasIngredient = isRealIngredient(ingredient);

  // A manually added, heading-only row remains presentational. Extracted
  // recipes may instead attach the heading to the first real ingredient.
  if (ingredient.sectionHeader != null && !hasIngredient) {
    return (
      <tr
        ref={setNodeRef}
        style={style}
        className={cn(
          "group border-b border-border/40 last:border-0",
          isDragging && "opacity-50 bg-muted/50"
        )}
      >
        <td className="w-6 pr-1 py-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
            tabIndex={-1}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </td>
        <td colSpan={4} className="py-1 pr-1">
          <Input
            value={ingredient.sectionHeader}
            onChange={(e) => onChange({ ...ingredient, sectionHeader: e.target.value || null })}
            placeholder="Section header (e.g. Filling:)"
            className="h-6 text-xs font-semibold bg-muted/50 border-dashed"
          />
        </td>
        <td className="py-1 w-7">
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
            tabIndex={-1}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <>
      {ingredient.sectionHeader != null && (
        <tr className="border-b border-border/20">
          <td className="w-6 pr-1 py-1" />
          <td colSpan={4} className="py-1 pr-1">
            <Input
              value={ingredient.sectionHeader}
              onChange={(e) =>
                onChange({ ...ingredient, sectionHeader: e.target.value || null })
              }
              placeholder="Section header (e.g. Filling:)"
              className="h-6 text-xs font-semibold bg-muted/50 border-dashed"
            />
          </td>
          <td className="py-1 w-7" />
        </tr>
      )}
      <tr
        ref={setNodeRef}
        style={style}
        className={cn(
          "group border-b border-border/40 last:border-0",
          isDragging && "opacity-50 bg-muted/50"
        )}
      >
        <td className="w-6 pr-1 py-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
            tabIndex={-1}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </td>
        <td className="py-1 pr-1 w-16">
          {scaleFactor !== 1 && ingredient.amount ? (
            <div
              title={`Original: ${ingredient.amount}`}
              className="h-6 flex items-center px-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded cursor-default truncate"
            >
              {scaleAmount(ingredient.amount, scaleFactor)}
            </div>
          ) : (
            <Input
              value={ingredient.amount ?? ""}
              onChange={(e) => set("amount", e.target.value)}
              placeholder="1/2"
              className="h-6 text-xs px-1.5"
            />
          )}
        </td>
        <td className="py-1 pr-1 w-20">
          <input
            type="text"
            list="unit-options"
            value={ingredient.unit ?? ""}
            onChange={(e) => set("unit", e.target.value)}
            placeholder="unit"
            className="h-6 w-full text-xs px-1.5 rounded border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </td>
        <td className="py-1 pr-1">
          <Input
            value={ingredient.item}
            onChange={(e) => onChange({ ...ingredient, item: e.target.value })}
            placeholder="Ingredient"
            className="h-6 text-xs px-1.5"
          />
        </td>
        <td className="py-1 pr-1 w-32">
          <Input
            value={ingredient.prepNotes ?? ""}
            onChange={(e) => set("prepNotes", e.target.value)}
            placeholder="prep notes"
            className="h-6 text-xs px-1.5 text-muted-foreground"
          />
        </td>
        <td className="py-1 w-7">
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
            tabIndex={-1}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
    </>
  );
}

interface IngredientsEditorProps {
  ingredients: LocalIngredient[];
  onChange: (ingredients: LocalIngredient[]) => void;
  scaleFactor?: number;
}

export function IngredientsEditor({ ingredients, onChange, scaleFactor = 1 }: IngredientsEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIdx = ingredients.findIndex((i) => i._key === active.id);
        const newIdx = ingredients.findIndex((i) => i._key === over.id);
        if (oldIdx !== -1 && newIdx !== -1) {
          const reordered = arrayMove(ingredients, oldIdx, newIdx).map(
            (ing, idx) => ({ ...ing, sortOrder: idx + 1 })
          );
          onChange(reordered);
        }
      }
    },
    [ingredients, onChange]
  );

  const update = (key: string, updated: LocalIngredient) => {
    onChange(ingredients.map((i) => (i._key === key ? updated : i)));
  };

  const remove = (key: string) => {
    onChange(
      ingredients
        .filter((i) => i._key !== key)
        .map((ing, idx) => ({ ...ing, sortOrder: idx + 1 }))
    );
  };

  const add = () => {
    const newKey = `new-${Date.now()}-${Math.random()}`;
    onChange([
      ...ingredients,
      {
        _key: newKey,
        sortOrder: ingredients.length + 1,
        amount: null,
        unit: null,
        item: "",
        prepNotes: null,
        sectionHeader: null,
      },
    ]);
  };

  const addSection = () => {
    const newKey = `new-${Date.now()}-${Math.random()}`;
    onChange([
      ...ingredients,
      {
        _key: newKey,
        sortOrder: ingredients.length + 1,
        amount: null,
        unit: null,
        item: "",
        prepNotes: null,
        sectionHeader: "Section:",
      },
    ]);
  };

  if (ingredients.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground italic py-2">
          No ingredients yet.
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={add} className="h-7 text-xs gap-1">
            <Plus className="h-3 w-3" /> Add Ingredient
          </Button>
          <Button size="sm" variant="outline" onClick={addSection} className="h-7 text-xs gap-1">
            <Plus className="h-3 w-3" /> Add Section
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <datalist id="unit-options">
        {UNITS.filter((u) => u !== "").map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-xs text-muted-foreground border-b">
              <th className="w-6" />
              <th className="text-left font-normal pb-1 pr-1 w-16">Amt</th>
              <th className="text-left font-normal pb-1 pr-1 w-16">Unit</th>
              <th className="text-left font-normal pb-1 pr-1">Ingredient</th>
              <th className="text-left font-normal pb-1 pr-1 w-32">Prep Notes</th>
              <th className="w-7" />
            </tr>
          </thead>
          <SortableContext
            items={ingredients.map((i) => i._key)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {ingredients.map((ing) => (
                <SortableRow
                  key={ing._key}
                  ingredient={ing}
                  onChange={(updated) => update(ing._key, updated)}
                  onDelete={() => remove(ing._key)}
                  scaleFactor={scaleFactor}
                />
              ))}
            </tbody>
          </SortableContext>
        </table>
      </DndContext>

      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={add} className="h-7 text-xs gap-1">
          <Plus className="h-3 w-3" /> Add Ingredient
        </Button>
        <Button size="sm" variant="outline" onClick={addSection} className="h-7 text-xs gap-1">
          <Plus className="h-3 w-3" /> Add Section
        </Button>
      </div>
    </div>
  );
}
